/* import __COLOCATED_TEMPLATE__ from './fly-date-picker.hbs.js'; */
var _dec, _dec2, _dec3, _dec4, _dec5, _dec6, _dec7, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _descriptor9, _descriptor10;
function _initializerDefineProperty(target, property, descriptor, context) { if (!descriptor) return; Object.defineProperty(target, property, { enumerable: descriptor.enumerable, configurable: descriptor.configurable, writable: descriptor.writable, value: descriptor.initializer ? descriptor.initializer.call(context) : void 0 }); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : String(i); }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }
function _initializerWarningHelper(descriptor, context) { throw new Error('Decorating class property failed. Please ensure that ' + 'transform-class-properties is enabled and runs after the decorators transform.'); }
import { action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { isNone, isPresent } from '@ember/utils';
import { next, scheduleOnce } from '@ember/runloop';
import { formatDate } from 'ember-power-calendar-utils';
import moment from 'moment-timezone';
import { DEFAULT_FORMAT, CUSTOM_RANGE, DEFAULT_RANGE_PICKER_PRESETS, MONTHS, YEARS } from '@customerio/fly-components/utils/fly-date-picker-utils';
import { LOCAL_TIME_ZONE, SELECTABLE_TIME_ZONES } from '@customerio/fly-components/utils/time-zones';
import calculatePosition from 'ember-basic-dropdown/utils/calculate-position';
// @ts-ignore
import PowerCalendar from 'ember-power-calendar/components/power-calendar';
// @ts-ignore
import PowerCalendarRange from 'ember-power-calendar/components/power-calendar-range';
import { MIN_UNIX_TIMESTAMP, MAX_UNIX_TIMESTAMP } from '@customerio/fly-components/utils/validators/timestamp';
import { ensureSafeComponent } from '@embroider/util';
function isRange(_selected, range) {
  // if "@range={{true}}" was passed, we can assume @selected to be a date range:
  return range === true;
}
let FlyDatePicker = (_dec = computed('args.selected'), _dec2 = computed('selectedDateRange.{start,end}'), _dec3 = computed('selectedDate'), _dec4 = computed('selectedDate', 'selectedHours', 'selectedMinutes', 'selectedSeconds', 'selectedAMPM', 'selectedTimezone'), _dec5 = computed('selectedDateTimeWithTimezone'), _dec6 = computed('range', 'selectedDateRange.{start,end}', 'selectedDateTimeWithTimezone'), _dec7 = computed('selectedDateRange.{start,end}', 'selectedDate', 'range'), (_class = class FlyDatePicker extends Component {
  get errorType() {
    return this.args.errorType ?? 'tooltip';
  }
  get selected() {
    return this.args.selected ?? (this.args.range ? {} : null);
  }
  get placeholder() {
    return this.args.placeholder ?? 'Choose a date…';
  }
  get range() {
    return this.args.range ?? false;
  }
  get horizontalPosition() {
    return this.args.horizontalPosition ?? 'auto';
  }
  get verticalPosition() {
    return this.args.verticalPosition ?? 'auto';
  }
  get size() {
    return this.args.size ?? 'normal';
  }
  get dateTime() {
    return this.args.dateTime ?? false;
  }
  get fluid() {
    return this.args.fluid ?? false;
  }
  get showPresets() {
    return this.args.showPresets ?? false;
  }
  get weekdayFormat() {
    return this.args.weekdayFormat ?? 'min';
  }
  get closeOnSelect() {
    return this.args.closeOnSelect ?? true;
  }
  get closeOnPresetSelect() {
    return this.args.closeOnPresetSelect ?? false;
  }
  get canClearSelection() {
    return this.args.canClearSelection ?? true;
  }
  get startOfWeek() {
    return this.args.startOfWeek ?? 1;
  }
  get presets() {
    return this.args.presets ?? DEFAULT_RANGE_PICKER_PRESETS;
  }

  // internal state

  get years() {
    let years = [...YEARS];
    if (!this.selected || isRange(this.selected, this.range)) {
      return years.map(String);
    }

    // if passed "selected" date (or range) is more in the future than highest selectable year:
    // add more options as needed
    let lastYear = years[years.length - 1];
    let targetLastYear = Math.max((this.selected.end || this.selected.start || this.selected)?.year() || lastYear);
    if (targetLastYear > lastYear) {
      let missingYears = [...Array(targetLastYear - lastYear).keys()].map(index => lastYear + index + 1);
      years.push(...missingYears);
    }

    // return as strings: it allows selected option to show correctly
    return years.map(String);
  }

  // selectedDateRange is an object with "start", "end" properties set to Moment.js date objects
  // it's used only in "range=true" component variant

  get selectedDateRangeNative() {
    return this.selectedDateRange != null ? {
      start: this.selectedDateRange?.start?.toDate() ?? null,
      end: this.selectedDateRange?.end?.toDate() ?? null
    } : null;
  }
  set selectedDateRangeNative(value) {
    this.selectedDateRange = {
      start: value?.start ? moment.tz(value.start, moment.tz.guess()) : null,
      end: value?.end ? moment.tz(value.end, moment.tz.guess()) : null
    };
  }

  // selectedDate is Moment.js date object always set to midnight in local time of selected date
  // when selecting single date or datetime (not range)

  get selectedDateNative() {
    return this.selectedDate?.toDate();
  }
  set selectedDateNative(value) {
    this.selectedDate = moment.tz(value, moment.tz.guess());
  }

  // time-related inputs:

  // reference property transforming between form input values and Moment.js date object holding full selected date and (optionally) time
  get selectedDateTimeWithTimezone() {
    if (!this.selectedDate) {
      return null;
    }
    let hours = Number(this.selectedHours);
    let ampm = this.selectedAMPM;
    if (ampm === 'pm' && hours !== 12) {
      hours += 12;
    } else if (hours === 12 && ampm === 'am') {
      hours = 0;
    }
    let date = moment.tz(this.selectedTimezone).year(this.selectedDate.year()).month(this.selectedDate.month()).date(this.selectedDate.date()).hours(hours).minutes(this.selectedMinutes).seconds(this.selectedSeconds);
    return date;
  }
  set selectedDateTimeWithTimezone(value) {
    if (value == null) {
      this.selectedHours = 12;
      this.selectedMinutes = 0;
      this.selectedSeconds = 0;
      this.selectedAMPM = 'am';
      this.selectedTimezone = LOCAL_TIME_ZONE.value;
      this.selectedDate = null;
      this.selectedTimestamp = '';
      return;
    }
    value = value.clone(); // ensure we don't mutate the original Moment.js date object

    this.selectedDate = value.clone().startOf('day');
    this.selectedHours = [0, 12].includes(value.hours()) ? 12 : value.hours() % 12;
    this.selectedMinutes = value.minutes();
    this.selectedSeconds = value.seconds();
    this.selectedAMPM = value.hours() >= 12 ? 'pm' : 'am';

    // select timezone of current datetime, fallback to local timezone
    // (won't ever happen in real-life usage, unless datetime was selected using different component allowing more timezones than fly-date-picker)
    this.selectedTimezone = (SELECTABLE_TIME_ZONES.find(tz => tz.matchingValues.includes(value?.tz())) || LOCAL_TIME_ZONE).value;

    // passed Moment date object has no explicitly set timezone: fallback it to browser local timezone instead
    if (!value.tz()) {
      value.tz(LOCAL_TIME_ZONE.value);
    }
  }
  get selectedTimestamp() {
    if (!this.selectedDateTimeWithTimezone) {
      return '';
    }
    return String(this.selectedDateTimeWithTimezone.unix());
  }
  set selectedTimestamp(value) {
    if (value) {
      this.selectedDateTimeWithTimezone = moment.unix(Number(value)).tz(this.selectedTimezone);
    }
  }
  get hasError() {
    return !isEmpty(this.args.errors);
  }
  get errorMessage() {
    return (this.args.errors || []).map(err => typeof err === 'string' ? err : err.message).join(' ');
  }
  get calendarComponent() {
    return ensureSafeComponent(this.args.range ? PowerCalendarRange : PowerCalendar, this);
  }
  constructor(owner, args) {
    super(owner, args);
    _initializerDefineProperty(this, "center", _descriptor, this);
    _defineProperty(this, "MONTHS", MONTHS);
    _initializerDefineProperty(this, "selectedDateRange", _descriptor2, this);
    _initializerDefineProperty(this, "selectedDate", _descriptor3, this);
    _initializerDefineProperty(this, "selectedHours", _descriptor4, this);
    _initializerDefineProperty(this, "selectedMinutes", _descriptor5, this);
    _initializerDefineProperty(this, "selectedSeconds", _descriptor6, this);
    _initializerDefineProperty(this, "selectedAMPM", _descriptor7, this);
    _initializerDefineProperty(this, "selectedTimezone", _descriptor8, this);
    // Selected preset:
    _initializerDefineProperty(this, "selectedPreset", _descriptor9, this);
    _initializerDefineProperty(this, "containerElement", _descriptor10, this);
    let {
      range,
      selected,
      presets
    } = this;
    let selectedRange = null;
    if (range) {
      selectedRange = selected;
    }
    if (range && this.showPresets && selected && selectedRange?.start && selectedRange?.end) {
      let selectedPreset = presets.find(preset => {
        if (!preset.getRange) {
          return false;
        }
        let presetRange = preset.getRange();
        if (!presetRange?.start || !presetRange?.end) {
          return false;
        }
        return moment(this.selected.start).isSame(moment(presetRange.start)) && moment(this.selected.end).isSame(moment(presetRange.end));
      });
      this.selectedPreset = selectedPreset ?? null;
    }
  }
  onOpen() {
    let center = moment();
    let {
      range,
      selected
    } = this;
    if (range) {
      if (selected.start) {
        center = selected.start;
      }
      this.selectedDateRange = selected;
      this.center = center;
      return;
    }
    if (selected) {
      center = selected;
      this.selectedTimestamp = String(selected.unix());
      this.selectedDateTimeWithTimezone = selected;
      this.center = selected;
    } else {
      this.selectedDateTimeWithTimezone = null;
      this.selectedTimezone = LOCAL_TIME_ZONE.value;
      this.center = center;
    }
    next(() => {
      let selectedDay = document.querySelector('.ember-power-calendar-day--selected');
      let today = document.querySelector('.ember-power-calendar-day--today');

      // safe navigation operator: in some acceptance test scenarios there will be neither "selectedDay", "today" elements
      (selectedDay || today)?.focus();
    });
  }
  setElement(containerElement) {
    this.containerElement = containerElement;
  }
  onClose() {
    this.args.onClose?.();
    scheduleOnce('afterRender', this, this.focusOnInputAfterClose);
  }
  focusOnInputAfterClose() {
    // in some re-rendering cases onClose can be called when main component is already removed from the DOM tree - in which case this.element will be null
    // this doesn't impact the user experience, but throws an error and breaks selected acceptance tests in UI
    // don't try to access it in such scenarios:
    if (this.containerElement == null) {
      return;
    }
    let inputElement = this.containerElement.querySelector('.js-date-picker-input');
    inputElement?.focus();
  }
  didReceiveAttrs() {
    if (this.range) {
      this.selectedDateRange = this.selected;
    } else {
      this.selectedDateTimeWithTimezone = this.selected;
    }
  }
  calculatePosition(trigger,
  // "any" because ember-basic-dropdown doesn't export its internal CalcaulatePositionOptions type:
  ...rest) {
    let position = calculatePosition(trigger, ...rest);
    let {
      height
    } = trigger.getBoundingClientRect();

    // When the date picker opens, we want it to cover the input, so we need to
    // translate the content up or down.
    if (position.verticalPosition === 'below') {
      position.style.top = position.style.top - height;
    } else {
      position.style.top = position.style.top + height;
    }
    return position;
  }
  get selectedText() {
    let {
      range,
      selectedDateTimeWithTimezone,
      selectedDateRange
    } = this;
    if (range) {
      if (isNone(selectedDateRange) || isNone(selectedDateRange.start)) {
        return;
      }
      let {
        start,
        end
      } = selectedDateRange;
      if (start && end) {
        return `${formatDate(start, DEFAULT_FORMAT)} - ${formatDate(end, DEFAULT_FORMAT)}`;
      } else if (start) {
        return `${formatDate(start, DEFAULT_FORMAT)}`;
      }
    }
    if (isNone(selectedDateTimeWithTimezone)) {
      return;
    }
    let selectedDateText = formatDate(selectedDateTimeWithTimezone, DEFAULT_FORMAT);
    if (this.dateTime) {
      // only include timezone abbreviation in parentheses if it's more descriptive than a number:
      let timeFormat = isNaN(Number(this.selectedDateTimeWithTimezone?.format('z'))) ? 'hh:mm:ssa (Z z)' : 'hh:mm:ssa (Z)';
      let selectedTimeText = this.selectedDateTimeWithTimezone?.format(timeFormat);
      return `${selectedDateText} at ${selectedTimeText}`;
    }
    return selectedDateText;
  }
  get hasSelection() {
    let {
      range,
      selectedDateRange,
      selectedDate
    } = this;
    if (range) {
      return !isNone(selectedDateRange) && isPresent(selectedDateRange.start) && isPresent(selectedDateRange.end);
    }
    return isPresent(selectedDate);
  }
  setCenter({
    date
  }) {
    this.center = date;
  }
  findCenter(type, value) {
    let center = this.center ? moment(this.center) : moment();
    if (type === 'month') {
      center = center.month(value);
    } else if (type === 'year') {
      center = center.year(Number(value));
    }
    return center;
  }
  onDateSelect(dropdown, calendar, ..._args) {
    this.selectedPreset = this.presets.find(preset => preset.value === CUSTOM_RANGE) ?? null;
    if (this.range) {
      this.selectedDateRangeNative = calendar.date;
    } else {
      this.selectedDateNative = calendar.date;
    }
    this.args.onSelect?.({
      date: this.range ? this.selectedDateRange : this.selectedDateTimeWithTimezone
    });
    if (this.closeOnSelect && this.hasSelection && !this.dateTime) {
      // eslint-disable-next-line ember/no-incorrect-calls-with-inline-anonymous-functions
      scheduleOnce('afterRender', this, () => {
        dropdown.actions.close();
      });
    }
  }
  emitSelectedDateTimeWithTimezone() {
    if (this.selectedDateTimeWithTimezone) {
      this.args.onSelect?.({
        date: this.selectedDateTimeWithTimezone
      });
    }
  }
  didChangeCenter(type, ev) {
    let {
      value
    } = ev.target;
    this.center = this.findCenter(type, value);
  }
  didChangeMonth(delta) {
    this.center = moment(this.center).add(delta, 'month');
  }
  didSelectPreset(dropdown, preset) {
    let date = preset.getRange();
    this.selectedPreset = preset;
    this.args.onSelect?.({
      date
    });
    if (this.closeOnPresetSelect && !this.dateTime) {
      // eslint-disable-next-line ember/no-incorrect-calls-with-inline-anonymous-functions
      scheduleOnce('afterRender', this, () => {
        dropdown.actions.close();
      });
    }
  }
  clearSelection(event) {
    event.stopPropagation();
    this.selectedPreset = null;
    this.selectedDateRange = null;
    this.selectedDateTimeWithTimezone = null;
    this.args.onSelect?.({
      date: null
    });
  }
  onHoursInput(event) {
    let value = Math.round(Number(event.target.value));
    if (value > 12 && value < 24) {
      // attempt at using 24-hour clock: anything above "12" is transformed to "PM" 12-hour clock
      value -= 12;
      this.selectedAMPM = 'pm';
    } else if (value >= 24 || value <= 0) {
      // "24" or bigger, (or invalid non-positive value): transform to midnight (12 AM)
      value = 12;
      this.selectedAMPM = 'am';
    }
    this.selectedHours = value;
    this.emitSelectedDateTimeWithTimezone();
  }
  onSecondsMinutesInput(field, event) {
    let value = Math.abs(Math.round(Number(event.target.value)));
    value = Math.min(59, value);
    value = Math.max(0, value);
    if (field === 'minutes') {
      this.selectedMinutes = value;
    } else if (field === 'seconds') {
      this.selectedSeconds = value;
    }
    this.emitSelectedDateTimeWithTimezone();
  }
  onAMPMInput(value) {
    this.selectedAMPM = value;
    this.emitSelectedDateTimeWithTimezone();
  }
  onTimestampInput(event) {
    let valueStr = event.target.value;
    if (valueStr === '' || isNaN(Number(valueStr))) {
      this.selectedTimestamp = '';
      return;
    }
    let value = Number(valueStr);
    value = Math.round(value);
    value = Math.max(MIN_UNIX_TIMESTAMP + 1, value);
    value = Math.min(MAX_UNIX_TIMESTAMP - 1, value);
    this.selectedTimestamp = String(value);

    // update shown year/month: timestamp can be from different one than currently selected
    this.center = this.selectedDateTimeWithTimezone;
    this.emitSelectedDateTimeWithTimezone();
    return;
  }
  onSelectTimezone(value) {
    this.selectedTimezone = value;
  }
  onDateTimeSubmit(dropdown) {
    this.emitSelectedDateTimeWithTimezone();
    dropdown.actions.close();
  }
}, (_descriptor = _applyDecoratedDescriptor(_class.prototype, "center", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "years", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "years"), _class.prototype), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "selectedDateRange", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "selectedDateRangeNative", [_dec2], Object.getOwnPropertyDescriptor(_class.prototype, "selectedDateRangeNative"), _class.prototype), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "selectedDate", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "selectedDateNative", [_dec3], Object.getOwnPropertyDescriptor(_class.prototype, "selectedDateNative"), _class.prototype), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "selectedHours", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 12;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "selectedMinutes", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 0;
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "selectedSeconds", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 0;
  }
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "selectedAMPM", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 'am';
  }
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "selectedTimezone", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return '';
  }
}), _descriptor9 = _applyDecoratedDescriptor(_class.prototype, "selectedPreset", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "selectedDateTimeWithTimezone", [_dec4], Object.getOwnPropertyDescriptor(_class.prototype, "selectedDateTimeWithTimezone"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectedTimestamp", [_dec5], Object.getOwnPropertyDescriptor(_class.prototype, "selectedTimestamp"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onOpen", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onOpen"), _class.prototype), _descriptor10 = _applyDecoratedDescriptor(_class.prototype, "containerElement", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "setElement", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setElement"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onClose", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onClose"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "focusOnInputAfterClose", [action], Object.getOwnPropertyDescriptor(_class.prototype, "focusOnInputAfterClose"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "didReceiveAttrs", [action], Object.getOwnPropertyDescriptor(_class.prototype, "didReceiveAttrs"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "calculatePosition", [action], Object.getOwnPropertyDescriptor(_class.prototype, "calculatePosition"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectedText", [_dec6], Object.getOwnPropertyDescriptor(_class.prototype, "selectedText"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "hasSelection", [_dec7], Object.getOwnPropertyDescriptor(_class.prototype, "hasSelection"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setCenter", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setCenter"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "findCenter", [action], Object.getOwnPropertyDescriptor(_class.prototype, "findCenter"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onDateSelect", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onDateSelect"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "emitSelectedDateTimeWithTimezone", [action], Object.getOwnPropertyDescriptor(_class.prototype, "emitSelectedDateTimeWithTimezone"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "didChangeCenter", [action], Object.getOwnPropertyDescriptor(_class.prototype, "didChangeCenter"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "didChangeMonth", [action], Object.getOwnPropertyDescriptor(_class.prototype, "didChangeMonth"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "didSelectPreset", [action], Object.getOwnPropertyDescriptor(_class.prototype, "didSelectPreset"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "clearSelection", [action], Object.getOwnPropertyDescriptor(_class.prototype, "clearSelection"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onHoursInput", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onHoursInput"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onSecondsMinutesInput", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onSecondsMinutesInput"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onAMPMInput", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onAMPMInput"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onTimestampInput", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onTimestampInput"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onSelectTimezone", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onSelectTimezone"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onDateTimeSubmit", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onDateTimeSubmit"), _class.prototype)), _class));
export { FlyDatePicker as default };